import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Shield, Lock, AlertTriangle, Clock } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@core/components/ui/card";
import { Button } from "@core/components/ui/button";
import { Link } from "react-router-dom";
import { useAuth } from "@core/contexts/AuthContext";
import { supabase } from "@core/lib/supabase";
import AccountPageTemplate from "./AccountPageTemplate";

type ResetState = "idle" | "loading" | "sent" | "error";

export default function AccountSecurity() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation("common");
  const [resetState, setResetState] = useState<ResetState>("idle");
  const [countdown, setCountdown] = useState(0);

  async function handlePasswordReset() {
    if (!user?.email) return;

    setResetState("loading");
    try {
      // Utilise notre edge function pour envoyer un email brandé DooDates
      // (au lieu de l'email par défaut de Supabase).
      const { error } = await supabase.functions.invoke("send-password-reset", {
        body: {
          email: user.email,
          redirectTo: `${window.location.origin}/auth/reset-password`,
          lang: i18n.language?.slice(0, 2),
        },
      });
      if (error) throw error;
      setResetState("sent");
      // Décompte de 5 secondes avant de revenir à idle
      setCountdown(5);
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setResetState("idle");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      setResetState("error");
      // Revenir à l'état idle après 3 secondes en cas d'erreur
      setTimeout(() => setResetState("idle"), 3000);
    }
  }

  return (
    <AccountPageTemplate
      title={t("account.security.title")}
      description={t("account.security.description")}
      testId="account-security-page"
    >
      <div className="space-y-6">
        {/* Section mot de passe */}
        <Card className="border-gray-800 bg-gray-900/80">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Lock className="w-5 h-5 text-blue-400" />
              {t("account.security.password.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-400">
              {t("account.security.password.description")}
            </p>

            {resetState === "sent" ? (
              <div
                className="flex items-start gap-3 p-3 bg-emerald-900/20 border border-emerald-700/40 rounded-lg"
                data-testid="reset-email-sent"
              >
                <Shield className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <p className="text-sm text-emerald-300">
                  {t("account.security.password.email_sent_prefix")}{" "}
                  <span className="font-medium">{user?.email}</span>.{" "}
                  {t("account.security.password.email_sent_suffix")}
                  {countdown > 0 && (
                    <span className="ml-2 text-emerald-400">
                      {t("account.security.password.countdown", {
                        count: countdown,
                      })}
                    </span>
                  )}
                </p>
              </div>
            ) : resetState === "error" ? (
              <div
                className="flex items-start gap-3 p-3 bg-red-900/20 border border-red-700/40 rounded-lg"
                data-testid="reset-email-error"
              >
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm text-red-300">
                    {t("account.security.password.error")}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1 h-auto p-0 text-red-400 hover:text-red-300"
                    onClick={() => setResetState("idle")}
                  >
                    {t("account.security.password.retry")}
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={handlePasswordReset}
                disabled={resetState === "loading"}
                data-testid="reset-password-button"
                className="gap-2"
              >
                <Lock className="w-4 h-4" />
                {resetState === "loading"
                  ? t("account.security.password.sending")
                  : t("account.security.password.send_button")}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Section sessions actives — placeholder */}
        <Card className="border-gray-800 bg-gray-900/80 opacity-60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Clock className="w-5 h-5 text-gray-400" />
              {t("account.security.sessions.title")}
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-400 font-normal">
                {t("account.security.sessions.coming_soon")}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p
              className="text-sm text-gray-500"
              data-testid="sessions-placeholder"
            >
              {t("account.security.sessions.placeholder")}
            </p>
          </CardContent>
        </Card>

        {/* Zone de danger */}
        <Card className="border-red-900/50 bg-red-950/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              {t("account.security.danger.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-gray-400">
              {t("account.security.danger.description")}
            </p>
            <Button
              variant="outline"
              className="border-red-700/60 text-red-400 hover:bg-red-950/40 hover:border-red-600 gap-2"
              asChild
              data-testid="delete-account-link"
            >
              <Link to="/data-control">
                <AlertTriangle className="w-4 h-4" />
                {t("account.security.danger.delete_button")}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </AccountPageTemplate>
  );
}
